import { StarRatingBar } from '@lastbrand/web-ui/star-rating-bar'
import { cx } from '@lastbrand/web-ui-styled-system/css'
import { useTranslation } from 'next-i18next'
import React from 'react'

import { TEST_LOCATORS } from '@src/constants/test-locators'
import { Link } from '@src/quince-components/src/atoms/link'
import { toStarRatingValue } from '@src/utils/star-rating.utils'

import * as styles from './product-name-ds.styles'

// Figma: Quince Design System 2.0 → Widgets → ProductName (node 8306:145026).
// `default` stacks the rating under the title (black stars + "( N REVIEWS )");
// `ratingSide` puts a compact rating next to the title (cantaloupe stars + "(N)").
export type ProductNameDSVariant = 'default' | 'ratingSide'

export interface ProductNameDSProps {
  productTitle: string
  brandName?: string
  brandLink?: string
  avgRating?: number
  reviewCount?: number
  hideRating?: boolean
  hideReviewCount?: boolean
  hideReviewLabel?: boolean
  showRatingValue?: boolean
  onReviewSummaryClick?: (e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => void
  variant?: ProductNameDSVariant
  titleAs?: 'h1' | 'h2' | 'h3' | 'p'
  className?: string
  titleClassName?: string
  reviewCountClassName?: string
}

const ProductNameDS = ({
  productTitle,
  brandName,
  brandLink,
  avgRating,
  reviewCount,
  hideRating,
  hideReviewCount,
  hideReviewLabel,
  showRatingValue = false,
  onReviewSummaryClick,
  variant = 'default',
  titleAs: TitleTag = 'h1',
  className,
  titleClassName,
  reviewCountClassName,
}: ProductNameDSProps) => {
  const { t } = useTranslation('common')
  const isRatingSide = variant === 'ratingSide'
  const showReviewLabel = !isRatingSide && !hideReviewLabel

  const handleRatingKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onReviewSummaryClick?.(e)
    }
  }

  return (
    <div
      data-testid="product-name"
      className={cx(isRatingSide ? styles.containerRatingSideCss : styles.containerDefaultCss, className)}
    >
      <div className={isRatingSide ? styles.headerRatingSideCss : styles.headerDefaultCss}>
        {brandName &&
          (brandLink ? (
            <Link href={brandLink} className={cx(styles.brandNameCss, styles.brandNameLinkCss)}>
              {brandName}
            </Link>
          ) : (
            <p className={styles.brandNameCss}>{brandName}</p>
          ))}
        <TitleTag className={cx(styles.titleCss, titleClassName)}>{productTitle}</TitleTag>
      </div>
      {!hideRating && !!avgRating && Number(reviewCount) > 0 && (
        <div
          className={isRatingSide ? styles.ratingRatingSideCss : styles.ratingDefaultCss}
          onClick={onReviewSummaryClick}
          role={onReviewSummaryClick ? 'button' : undefined}
          tabIndex={onReviewSummaryClick ? 0 : undefined}
          onKeyDown={onReviewSummaryClick ? handleRatingKeyDown : undefined}
        >
          <span data-testid={TEST_LOCATORS.FIRST_FOLD_REVIEW_STARS} className={styles.starsWrapperCss}>
            <StarRatingBar
              value={toStarRatingValue(avgRating)}
              variant={isRatingSide ? 'cantaloupe' : 'black'}
              aria-label={t('out_of_5_stars', { rating: avgRating.toFixed(1) })}
            />
          </span>
          {showRatingValue && <span className={styles.ratingValueCss}>{avgRating.toFixed(1)}</span>}
          {!hideReviewCount && (
            <p
              data-testid={TEST_LOCATORS.FIRST_FOLD_REVIEW_COUNT}
              className={cx(styles.reviewCountCss, reviewCountClassName)}
            >
              (
              <span className={styles.reviewCountTextCss}>
                {reviewCount}
                {showReviewLabel && <>&nbsp;{reviewCount === 1 ? t('review') : t('reviews_v1')}</>}
              </span>
              )
            </p>
          )}
        </div>
      )}
    </div>
  )
}

export default ProductNameDS
